// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import { useCallback, useState, type ReactNode } from 'react';

import Button, { IconButton } from '@atlaskit/button/new';

import { cssMap } from '@atlaskit/css';

import CrossIcon from '@atlaskit/icon/core/cross';

import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { token } from '@atlaskit/tokens';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

export type CrossflowRecommendationBannerProps = {
	/** The product icon to display */
	icon: ReactNode;
	/** The banner title text */
	title: string;
	/**
	 * The banner description content. May be plain text, or a ReactNode
	 * (e.g. a `<FormattedMessage>` containing inline links / chunks).
	 */
	description: ReactNode;
	/** The CTA button label */
	buttonName: string;
	/** The dismiss button accessible label */
	dismissLabel: string;
	/** Called when the CTA button is clicked (e.g. to open crossflow) */
	onCtaClick: () => void;
	/** Called by message template to dismiss itself from the UI */
	dismiss: () => void;
	analyticsId: string;
};

const styles = cssMap({
	bannerWrapper: {
		padding: token('space.200'),
		borderRadius: token('radius.small'),
		width: '100%',
		boxSizing: 'border-box',
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	iconContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '40px',
		height: '40px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.discovery'),
	},
	content: {
		flex: 1,
		minWidth: '0px',
	},
	textContainer: {
		flex: 1,
		minWidth: '0px',
	},
	actions: {
		flexShrink: 0,
	},
});

export const CrossflowRecommendationBanner = ({
	icon,
	title,
	description,
	buttonName,
	dismissLabel,
	onCtaClick,
	dismiss,
	analyticsId,
}: CrossflowRecommendationBannerProps): JSX.Element | null => {
	const { messageClicked, messageDismissed } = useMessageEvent();
	const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const [isDismissed, setIsDismissed] = useState(false);

	const handleDismiss = useCallback(() => {
		setIsDismissed(true);
		void messageDismissed();
		onClosed({ interactionIdentifier: 'dismissButton', additionalMetadata: {} });
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: `${analyticsId}DismissButton`,
			attributes: {
				buttonType: 'secondary',
			},
		});
		dismiss();
	}, [messageDismissed, onClosed, fireAnalyticsEvent, analyticsId, dismiss]);

	const handleCtaClick = useCallback(() => {
		void messageClicked();
		onCtaActioned({
			interactionIdentifier: 'ctaButton',
			additionalMetadata: { actionTaken: true },
		});
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: `${analyticsId}CtaButton`,
			attributes: {
				buttonType: 'primary',
			},
		});
		onCtaClick();
	}, [onCtaClick, messageClicked, onCtaActioned, fireAnalyticsEvent, analyticsId]);

	if (isDismissed) {
		return null;
	}

	return (
		<Inline
			alignBlock="center"
			spread="space-between"
			space="space.200"
			xcss={styles.bannerWrapper}
			testId="crossflow-recommendation-banner"
		>
			<Inline alignBlock="center" space="space.200" xcss={styles.content}>
				<Box xcss={styles.iconContainer}>{icon}</Box>
				<Stack space="space.050" xcss={styles.textContainer}>
					<Text as="span" weight="semibold">
						{title}
					</Text>
					<Text as="span" color="color.text.subtlest">
						{description}
					</Text>
				</Stack>
			</Inline>
			<Inline alignBlock="center" space="space.100" xcss={styles.actions}>
				<Button onClick={handleCtaClick}>{buttonName}</Button>
				<IconButton
					appearance="subtle"
					icon={CrossIcon}
					label={dismissLabel}
					onClick={handleDismiss}
				/>
			</Inline>
		</Inline>
	);
};
